/**
 * Tier-4 rules that act when an enemy dies (M21, docs/TOWERS_V2.md "Tier 4"): the Spirit Totem's healing and Soul
 * Harvest, the Doom Totem's Doom Mark, the Plague Cauldron's Contagion. They run only once a branch is chosen.
 */
import { DamageType, isHex } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { distanceSquared } from "../../world/roadGeometry";
import { SimConstants } from "../constants";
import { Enemy, Tower } from "../entities";
import { SimEventKind } from "../events";
import type { LevelSim } from "../levelSim";
import { applyDamage } from "../systems/combat";
import { isBranch, skill } from "./ascension";
import { applyDot } from "./statuses";

/** The totem whose curse the dying enemy wore, when it is a tier-4 one. */
function curseTotem(sim: LevelSim, e: Enemy): Tower | null {
  if (e.hexTowerId === 0) return null;
  const t = sim.findTower(e.hexTowerId);
  return t !== null && t.tier > 3 && isHex(sim.balance.towers[t.archetype]) ? t : null;
}

/** Kill points after Soul Harvest (a Spirit Totem's curse: +pointsBonus, floored). */
export function branchKillPoints(sim: LevelSim, e: Enemy, points: number): number {
  const t = curseTotem(sim, e);
  const s = t === null ? null : skill(sim, t, "soulHarvest");
  return s === null ? points : Fix64.fromInt(points).mul(Fix64.One.add(s.params.num("pointsBonus"))).floorToInt();
}

/** After a kill: the curse's death rules, then Contagion. */
export function onBranchDeath(sim: LevelSim, e: Enemy): void {
  const t = curseTotem(sim, e);
  if (t !== null) {
    const spirit = isBranch(sim, t, "spirit");
    if (spirit !== null) {
      t.shotCount++;                                       // a Spirit Totem counts the cursed deaths it has seen
      const every = Math.max(1, spirit.tier4.int("healEvery", 1));
      if (t.shotCount % every === 0) healFortress(sim, t, spirit.tier4.int("healAmount", 1));
    }
    const mark = skill(sim, t, "doomMark");
    if (mark !== null) doomBlast(sim, e, t, mark.index, e.maxHp.mul(mark.params.num("fraction")), mark.params.num("radius"));
  }
  contagion(sim, e);
}

/** The fortress gains `amount` HP up to its maximum (FortressHealed; nothing when already full). */
export function healFortress(sim: LevelSim, t: Tower, amount: number): void {
  const before = sim.baseHp;
  sim.baseHp = Math.min(sim.maxBaseHp, sim.baseHp + amount);
  if (sim.baseHp > before) sim.events.add(SimEventKind.FortressHealed, sim.baseHp - before, t.id, sim.baseHp);
}

/** Doom Mark: the dead enemy bursts for `damage` magic on every enemy within `radius` (credited to the totem). */
function doomBlast(sim: LevelSim, dead: Enemy, t: Tower, skillIndex: number, damage: Fix64, radius: Fix64): void {
  sim.events.add(SimEventKind.SkillFired, t.id, skillIndex, dead.id);
  const r2 = radius.mul(radius);
  sim.areaDepth++;
  for (const e of sim.enemySlots) {
    if (!e.alive || e === dead || e.burrowed || distanceSquared(dead.x, dead.y, e.x, e.y).gt(r2)) continue;
    applyDamage(sim, e, t, damage, DamageType.Magic);
  }
  sim.areaDepth--;
}

/**
 * Contagion: a shredded enemy dying within reach of a Plague Cauldron that knows it passes its shred stacks (each the
 * Poison tower's shredPerHit) and a poison to the `spreadTo` nearest ground enemies within `reach` (nearest first, ties →
 * lower id). The lowest-id such cauldron is credited.
 */
function contagion(sim: LevelSim, dead: Enemy): void {
  if (dead.shredStacks <= 0) return;
  let src: Tower | null = null, s = null;
  for (const t of sim.towerSlots) {
    if (!t.alive || isBranch(sim, t, "plague") === null) continue;
    const c = skill(sim, t, "contagion");
    if (c === null) continue;
    const r = sim.rangeOf(t);
    if (distanceSquared(Fix64.fromInt(t.cell.x), Fix64.fromInt(t.cell.y), dead.x, dead.y).gt(r.mul(r))) continue;
    src = t; s = c; break;
  }
  if (src === null || s === null) return;
  const reach = s.params.num("reach"), r2 = reach.mul(reach);
  const near: Enemy[] = [];
  for (const e of sim.enemySlots) if (e.alive && !e.isAir && !e.burrowed && e !== dead && distanceSquared(dead.x, dead.y, e.x, e.y).le(r2)) near.push(e);
  near.sort((p, q) => distanceSquared(dead.x, dead.y, p.x, p.y).cmp(distanceSquared(dead.x, dead.y, q.x, q.y)) || p.id - q.id);
  const a = sim.balance.towers[src.archetype];
  const perTick = sim.damageOf(src).mul(s.params.num("dotPerSecondMult")).divInt(SimConstants.TicksPerSecond);
  const n = Math.min(near.length, s.params.int("spreadTo"));
  if (n === 0) return;
  sim.events.add(SimEventKind.SkillFired, src.id, s.index, dead.id);
  for (let i = 0; i < n; i++) {
    const e = near[i];
    if (e.shredStacks < dead.shredStacks) {
      e.shredArmour = e.shredArmour.add(a.shredPerHit.mulInt(dead.shredStacks - e.shredStacks));
      e.shredStacks = dead.shredStacks;
      sim.events.add(SimEventKind.EnemyShredded, e.id, src.id, e.shredStacks);
    }
    applyDot(e, src, perTick, s.params.int("dotTicks"));
    sim.events.add(SimEventKind.ChainHit, e.id, src.id, i + 1);
  }
}
